import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FileService, FilesResponse } from '../../services/file.service';
import { LoadingService } from '../../services/loading.service';
import { BlogHeaderComponent } from '../blog/blog-header/blog-header.component';
import { BlogFeedComponent } from '../blog/blog-feed/blog-feed.component';
import { LayoutSwitcherComponent } from '../blog/layout-switcher/layout-switcher.component';

/**
 * Public homepage shell.
 *
 * Phase-1 of the UI/UX refresh: this component is now a thin orchestrator.
 * It fetches bundles and renders <app-blog-header /> + <app-blog-feed />.
 * The old eager-fetch / floating-header / popup logic was removed; viewers
 * now lazy-load via BundleViewerService.
 *
 * The @Input properties below are kept as no-ops so AppHomeComponent
 * (which embeds <app-blog-home> with these inputs) does not break.
 */
@Component({
  selector: 'app-blog-home',
  standalone: true,
  imports: [CommonModule, BlogHeaderComponent, BlogFeedComponent, LayoutSwitcherComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="blog-shell">
      <app-blog-header *ngIf="!hideInternalNavigation">
        <app-layout-switcher></app-layout-switcher>
      </app-blog-header>
      <app-blog-feed [bundles]="bundles" [loading]="loading"></app-blog-feed>
    </div>
  `,
  styleUrl: './blog-home.component.css',
})
export class BlogHomeComponent implements OnInit, OnDestroy {
  // Kept for backwards-compatibility with AppHomeComponent's template + ViewChild reads.
  @Input() selectedTopicFromTree: any = null;
  @Input() selectedSubjectFromTree: any = null;
  @Input() selectedSectionFromTree: any = null;
  @Input() isBlogMode = true;
  @Input() hideInternalNavigation = false;

  /** No-op in the new layout (the floating header has been removed). Kept so AppHomeComponent's writes don't NPE. */
  isFloatingHeaderMode = false;

  bundles: FilesResponse[] = [];
  loading = true;

  private fileService = inject(FileService);
  private loadingService = inject(LoadingService);
  private cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.fileService.getAllFileBundles().subscribe({
      next: (bundles) => {
        this.bundles = Array.isArray(bundles) ? bundles : [];
        this.loading = false;
        this.dismissPreloader();
        this.cdr.markForCheck();
      },
      error: () => {
        this.bundles = [];
        this.loading = false;
        this.dismissPreloader();
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private dismissPreloader(): void {
    this.loadingService.setSingleTreeLoaded();
    this.loadingService.setMainAreaLoaded();
    this.loadingService.setDetailsAreaLoaded();
  }
}
